// React
import { useContext } from "react"

// Components
import { ActionButton, BaseTable, Button, ErrorText, Loading, Modal, Select } from "components"

// 
import { SuccessContext } from "contexts"

// Form
import { ErrorMessage } from "@hookform/error-message"
import { useFieldArray, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Icons
import { IoSendSharp } from "react-icons/io5"
import { TbPlus } from "react-icons/tb"

// Third-Party Libraries
import toast from "react-hot-toast"

// Types
import type { DataType } from "../types"

// Utils
import { useApi, useToggle } from "utils"
import { type FormType, ItemContext, useCoaList, validationSchema, useSelectCoa } from "../utils"

export function Create(): JSX.Element {
  // Hooks
  const { isActive, toggle } = useToggle(false)

  return (
    <>
      <button onClick={toggle}>
        <IoSendSharp className="text-center" />
      </button>

      {isActive && <ModalSection toggle={toggle} />}
    </>
  )
}

function ModalSection(params: {
  toggle: () => void
}): JSX.Element {
  // Hooks
  const api = useApi()
  const item = useContext(ItemContext)
  const onSuccess = useContext(SuccessContext)
  const { data, isLoading } = useCoaList()

  // Form
  const defaultValues: FormType = {
    data_coa: item.datacoa.map(item => ({
      coa_id: item.coa_id.toString(),
      dashboard_link_coa_id: item.dashboard_link_coa_id
    }))
  }
  const { control, formState, handleSubmit } = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })
  const onSubmit = (value: FormType): Promise<void> => {
    return new Promise<void>((resolve) => {
      toast.promise(
        api.post("/dashboardlink/update", {
          dashboard_link_code: item.dashboard_link_code,
          dashboard_link_id: item.dashboard_link_id,
          dashboard_link_name: item.dashboard_link_name,
          description: item.description,
          data_coa: value.data_coa.map(coa_item => {
            // Vars
            const dashboard_link_coa_id = formState.defaultValues?.data_coa?.find(i => i?.coa_id === coa_item.coa_id)?.dashboard_link_coa_id ?? null

            return {
              dashboard_link_coa_id,
              dashboard_link_id: item.dashboard_link_id,
              coa_id: coa_item.coa_id
            }
          })
        }),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message
        }
      ).then(() => {
        params.toggle()
        onSuccess()
      }).catch(() => {}).finally(resolve)
    })
  }
  const { fields, append, remove } = useFieldArray({
    control,
    name: "data_coa"
  })

  return (
    <Modal
      isOpen
      title="DATA ENTRY | LINKED ACCOUNT"
      closeModal={params.toggle}
    >
      <Modal.Body>
        <section className="flex flex-col gap-3">
          <CoaSelect
            data={data}
            isLoading={isLoading}
            onAddRow={coa_id => {
              if (fields.find(item => item.coa_id === coa_id)) {
                return toast.error("COA is already selected")
              }

              append({
                coa_id,
                dashboard_link_coa_id: null
              })
            }}
          />

          <table className="font-bold table-top">
            <tbody>
              <tr>
                <td className="w-[1px] whitespace-nowrap">COMPONENT CODE</td>
                <td className="w-[1px] px-3">:</td>
                <td>{item.dashboard_link_code}</td>
              </tr>

              <tr>
                <td className="w-[1px] whitespace-nowrap">COMPONENT NAME</td>
                <td className="w-[1px] px-3">:</td>
                <td>{item.dashboard_link_name}</td>
              </tr>

              <tr>
                <td className="w-[1px] whitespace-nowrap">DESCRIPTION</td>
                <td className="w-[1px] px-3">:</td>
                <td>{item.description ?? "-"}</td>
              </tr>
            </tbody>
          </table>

          <BaseTable>
            <thead>
              <tr>
                <th>NO</th>
                <th>NUMBER</th>
                <th>COA NAME</th>
                <th>ACTION</th>
              </tr>
            </thead>

            <tbody>
              {!fields.length ? (
                <tr>
                  <td colSpan={4}>
                    <Loading errorText="No data available" />
                  </td>
                </tr>
              ) : fields.map((item, key) => {
                // Vars
                const selectedCoa = data.find(i => i.value === item.coa_id)

                return (
                  <tr key={item.id}>
                    <td className="text-center">{key + 1}</td>
                    <td>{selectedCoa?.coa_code}</td>
                    <td>{selectedCoa?.coa_name}</td>
                    <td>
                      <section className="flex justify-center">
                        <ActionButton.Delete onClick={() => remove(key)} />
                      </section>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </BaseTable>

          <ErrorMessage
            name="data_coa"
            errors={formState.errors}
            render={({ message }) => <ErrorText text={message} />}
          />
        </section>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          color="primary"
          loading={formState.isSubmitting ? "true" : undefined}
          onClick={handleSubmit(onSubmit)}
        >
          SAVE
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function CoaSelect(params: {
  data: DataType[]
  isLoading: boolean
  onAddRow: (coa_id: string) => void
}): JSX.Element {
  // Hooks
  const { value, onChange } = useSelectCoa()

  return (
    <section className="flex items-end gap-3">
      <section className="grow">
        <Select
          label="Account"
          placeholder="Select Account"
          isLoading={params.isLoading}
          options={params.data}
          onChange={res => {
            if (res) {
              onChange(res.value)
            }
          }}
        />
      </section>

      <Button
        color="primary"
        onClick={() => {
          if (value) {
            params.onAddRow(value)
          }
        }}
      >
        <TbPlus />
      </Button>
    </section>
  )
}